import type { Level } from "@indeed/signals-schema/com/indeed/signals/schema/careerscout/Level";
import type { OperationalEventV2 } from "@indeed/signals-schema/com/indeed/signals/schema/careerscout/OperationalEventV2";

import { OperationalErrorSource as OperationalErrorSourceEnum } from "@indeed/signals-schema/com/indeed/signals/schema/careerscout/OperationalErrorSource";
import { OperationalSource as OperationalSourceEnum } from "@indeed/signals-schema/com/indeed/signals/schema/careerscout/OperationalSource";

/** Resolves chat-backend attribution for operational error metrics. */
export type OperationalSourceTag = keyof typeof OperationalSourceEnum;

const CHAT_BACKEND_SOURCES = new Set<string>([
  "useCareerScoutAPIBackend",
  "useCareerScoutAPIGetConversation",
  "useCareerScoutAPIListConversations",
  "useThreadLifecycle",
  "useHistoricalMessages",
  "useStreamStallRecovery",
  "useFindExistingViewjobConversation",
  "ExistingChat",
]);

const ACE_AI_CORE_PROCTOR = "ace_ai_core";
const ACE_AI_CORE_TEST_BUCKETS = new Set(["1", "test"]);

function isAceAiCoreTestBucketValue(bucketValue: string): boolean {
  return ACE_AI_CORE_TEST_BUCKETS.has(bucketValue);
}

/** Parses one proctor logging group from `getProctorLoggingString()`. */
function matchesAceAiCoreAllocation(group: string): boolean {
  const trimmed = group.trim();
  if (!trimmed) {
    return false;
  }

  // Allocation-ID prefix from native/web logging strings, e.g. `#A1:ace_ai_core1`.
  const withoutAllocationPrefix = trimmed.startsWith("#")
    ? trimmed.replace(/^#[^:]+:/, "")
    : trimmed;

  if (withoutAllocationPrefix.startsWith(`${ACE_AI_CORE_PROCTOR}:`)) {
    const [, bucket] = withoutAllocationPrefix.split(":");
    return bucket !== undefined && isAceAiCoreTestBucketValue(bucket);
  }

  // Forced/allocation-concatenated forms, e.g. `ace_ai_core1`.
  if (withoutAllocationPrefix.startsWith(ACE_AI_CORE_PROCTOR)) {
    const bucketSuffix = withoutAllocationPrefix.slice(ACE_AI_CORE_PROCTOR.length);
    if (bucketSuffix.length === 0) {
      return false;
    }
    return isAceAiCoreTestBucketValue(bucketSuffix);
  }

  return false;
}

export function isAceAiCoreTestBucket(proctorGroupsString: string): boolean {
  if (!proctorGroupsString) {
    return false;
  }

  return proctorGroupsString.split(",").some((group) => matchesAceAiCoreAllocation(group));
}

export interface OperationalMetricInput {
  source: string;
  errorSource?: string;
  reason?: string;
  status?: number;
  level: keyof typeof Level;
  proctorGroupsString?: string;
  operationalSource?: OperationalSourceTag;
  operationalErrorSource?: keyof typeof OperationalErrorSourceEnum;
}

export function resolveOperationalSource({
  source,
  proctorGroupsString,
  operationalSource,
}: Pick<
  OperationalMetricInput,
  "source" | "proctorGroupsString" | "operationalSource"
>): OperationalSourceTag {
  if (operationalSource) {
    return operationalSource;
  }

  if (CHAT_BACKEND_SOURCES.has(source)) {
    return isAceAiCoreTestBucket(proctorGroupsString ?? "")
      ? "AI_CORE"
      : OperationalSourceEnum.MASTRA;
  }

  if (source.toLowerCase().includes("auth")) {
    return OperationalSourceEnum.AUTH;
  }

  if (source.toLowerCase().includes("graphql")) {
    return OperationalSourceEnum.GRAPHQL;
  }

  return OperationalSourceEnum.OTHER;
}

export function resolveOperationalErrorSource({
  errorSource,
  reason,
  status,
  level,
  operationalErrorSource,
}: Pick<
  OperationalMetricInput,
  "errorSource" | "reason" | "status" | "level" | "operationalErrorSource"
>): keyof typeof OperationalErrorSourceEnum | null {
  if (operationalErrorSource) {
    return operationalErrorSource;
  }

  if (level !== "ERROR") {
    return null;
  }

  const normalizedErrorSource = (errorSource ?? "").toLowerCase();
  const normalizedReason = (reason ?? "").toLowerCase();
  const combined = `${normalizedErrorSource} ${normalizedReason}`;

  if (
    normalizedReason === "data_rate_limit" ||
    combined.includes("rate limit") ||
    normalizedErrorSource === "ratelimiterror"
  ) {
    return OperationalErrorSourceEnum.OTHER;
  }

  if (
    normalizedReason === "stream_protocol_validation" ||
    normalizedErrorSource === "ai_typevalidationerror" ||
    normalizedErrorSource === "riskassessmentblocked" ||
    combined.includes("validation") ||
    combined.includes("forbidden")
  ) {
    return OperationalErrorSourceEnum.VALIDATION;
  }

  if (
    combined.includes("network") ||
    combined.includes("fetch") ||
    combined.includes("timeout") ||
    normalizedErrorSource === "aborterror" ||
    normalizedErrorSource === "networkerror" ||
    (status !== undefined && (status === 0 || status >= 500))
  ) {
    return OperationalErrorSourceEnum.NETWORK;
  }

  if (
    combined.includes("parse") ||
    combined.includes("json") ||
    normalizedErrorSource === "syntaxerror"
  ) {
    return OperationalErrorSourceEnum.PARSE;
  }

  if (normalizedErrorSource === "careerscoutstreamerror") {
    return OperationalErrorSourceEnum.OTHER;
  }

  if (!errorSource && !reason && status === undefined) {
    return OperationalErrorSourceEnum.UNHANDLED;
  }

  return OperationalErrorSourceEnum.UNHANDLED;
}

export function operationalSourceToAvro(
  operationalSource: OperationalSourceTag | null,
): OperationalEventV2["operationalSource"] {
  if (!operationalSource) {
    return null;
  }

  return {
    "com.indeed.signals.schema.careerscout.OperationalSource": operationalSource,
  };
}

export function operationalErrorSourceToAvro(
  operationalErrorSource: keyof typeof OperationalErrorSourceEnum | null,
): OperationalEventV2["operationalErrorSource"] {
  if (!operationalErrorSource) {
    return null;
  }

  return {
    "com.indeed.signals.schema.careerscout.OperationalErrorSource": operationalErrorSource,
  };
}
